/**
 * BL-1912 — which pricing basis and currency a selection of lots shares, so one
 * bulk price is only ever set across lots where it means the same amount (ADR 035
 * — Money Units: a per-acre lot's start price and reserve are per-acre). Pure and
 * client-safe (ADR 010 — Isomorphic Services): the server refuses on it and the
 * Edit lots form disables its price rows on the same answer.
 */

import type { SelectionPricingBasis } from '@auctogon/core-admin/schemas/responses/lot-batch';

/** Refusal / disabled-row copy when a selection mixes per-acre and whole-price lots (AC 8). */
export const PRICING_BASIS_MIXED_MESSAGE = 'Select lots priced the same way (per acre or whole) to set prices together.';

/** Refusal / disabled-row copy when a price's currency is not the lots' own. */
export const PRICE_CURRENCY_MISMATCH_MESSAGE = 'Select lots priced in one currency to set prices together.';

/**
 * The pricing basis every lot shares. An unset `pricing_type` is whole-price,
 * as it is everywhere else (`isPerAcrePricing`).
 *
 * @param rows The selected lots' pricing types.
 * @returns `per_acre`, `total`, or `mixed`.
 * @sideEffect none — pure.
 */
export function selectionPricingBasis(rows: readonly { pricingType: string | null }[]): SelectionPricingBasis {
  const perAcre = rows.filter((r) => r.pricingType === 'per_acre').length;
  if (perAcre === 0) return 'total';
  return perAcre === rows.length ? 'per_acre' : 'mixed';
}

/**
 * The one currency every lot is priced in.
 *
 * @param rows The selected lots' currencies.
 * @returns The shared currency, or null when the lots differ (or none are selected).
 * @sideEffect none — pure.
 */
export function selectionCurrency(rows: readonly { currency: string }[]): string | null {
  const currencies = new Set(rows.map((r) => r.currency));
  return currencies.size === 1 ? [...currencies][0] : null;
}
